import { Field } from "../ui/field";
import { Textarea } from "../ui/textarea";
import type { Chapter } from "../../types";
import { ExtractToolbar } from "./ExtractToolbar";
import { PanelHeader } from "./PanelHeader";

type ModelOption = { value: string; label: string };

export function ChapterBriefPanel({
  activeChapter,
  busy,
  model,
  onModelChange,
  openAiModels,
  anthropicModels,
  onGenerate,
  onChange,
}: {
  activeChapter: Chapter;
  busy: boolean;
  model: string;
  onModelChange: (model: string, provider: "openai" | "anthropic") => void;
  openAiModels: readonly ModelOption[];
  anthropicModels: readonly ModelOption[];
  onGenerate: () => void;
  onChange: (value: string) => void;
}) {
  const brief = activeChapter.tabData.summary;
  const wordCount = brief.trim() ? brief.trim().split(/\s+/).length : 0;

  return (
    <section
      className="flex h-full min-h-0 flex-col overflow-hidden"
      aria-label="Chapter brief"
    >
      <PanelHeader
        title="Chapter Brief"
        description="The canonical, editable synopsis for planning, exports, and AI context. Aim for 120–220 factual words."
        actions={
          <ExtractToolbar
            model={model}
            onModelChange={onModelChange}
            openAiModels={openAiModels}
            anthropicModels={anthropicModels}
            onExtract={onGenerate}
            extractBusy={busy}
            extractLabel={brief.trim() ? "Regenerate brief" : "Generate brief"}
            extractBusyLabel="Generating brief…"
            modelClassName="w-44"
          />
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="flex max-w-3xl flex-col gap-2">
          <Field
            label="Detailed chapter synopsis"
            hint="Central events and outcome, character goal and obstacle, meaningful decisions, reveals, and unresolved tension."
            htmlFor="chapter-brief-field"
          >
            <Textarea
              id="chapter-brief-field"
              aria-label="Chapter brief"
              className="min-h-80 resize-y bg-card text-[0.95rem] leading-relaxed"
              placeholder="Generate a brief or write an accurate synopsis of this chapter…"
              value={brief}
              onChange={(event) => onChange(event.target.value)}
            />
          </Field>
          <p className="text-xs text-muted-foreground">
            {wordCount} {wordCount === 1 ? "word" : "words"} · You can freely edit generated text.
          </p>
        </div>
      </div>
    </section>
  );
}
